import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/store/auth'
import styles from './Milestones.module.css'

const STORAGE_KEY = 'mellomap-milestones'
const AGE_RANGES = ['2-3yr', '3-4yr', '4-5yr', '5-6yr']
const CATEGORIES = [
  { key: 'physical', label: 'Physical', emoji: '', color: '#7BA17B' },
  { key: 'cognitive', label: 'Cognitive', emoji: '', color: '#E07A5F' },
  { key: 'language', label: 'Language', emoji: '', color: '#5B8DEF' },
  { key: 'social', label: 'Social-Emotional', emoji: '', color: '#9B59B6' },
]

const MILESTONES: Record<string, Record<string, string[]>> = {
  '2-3yr': {
    physical: ['Runs confidently', 'Kicks a ball', 'Climbs stairs with support', 'Turns pages of a book', 'Uses spoon and fork'],
    cognitive: ['Sorts by color or shape', 'Completes 3-4 piece puzzles', 'Follows 2-step instructions', 'Engages in pretend play', 'Matches objects'],
    language: ['Uses 2-3 word sentences', 'Names familiar objects', 'Asks "why?"', 'Knows own name', 'Vocabulary of 200+ words'],
    social: ['Plays alongside other children', 'Expresses emotions with words', 'Begins sharing', 'Shows empathy', 'Asserts independence ("I do it!")'],
  },
  '3-4yr': {
    physical: ['Hops on one foot', 'Catches a bounced ball', 'Uses scissors', 'Draws circles and lines', 'Pedals a tricycle'],
    cognitive: ['Counts to 10', 'Understands same/different', 'Remembers parts of stories', 'Understands morning/night', 'Sorts objects by size'],
    language: ['Speaks in 4-5 word sentences', 'Tells simple stories', 'Asks lots of questions', 'Knows some colors and numbers', 'Understood by strangers 75%'],
    social: ['Takes turns in games', 'Shows concern for crying friend', 'Plays cooperatively', 'Follows simple rules', 'Has imaginary friends'],
  },
  '4-5yr': {
    physical: ['Stands on one foot 5+ seconds', 'Uses fork and knife', 'Draws person with 3+ body parts', 'Bounces and catches ball', 'Buttons and unbuttons'],
    cognitive: ['Counts 10+ objects', 'Draws recognizable shapes', 'Understands before/after', 'Groups objects by category', 'Predicts what happens next'],
    language: ['Speaks clearly in full sentences', 'Tells longer stories', 'Uses future tense', 'Asks what/when/how questions', 'Rhymes words'],
    social: ['Wants to please friends', 'Follows rules in board games', 'Distinguishes fantasy from reality', 'Shows more independence', 'Negotiates solutions'],
  },
  '5-6yr': {
    physical: ['Skips', 'Ties shoelaces with help', 'Writes some letters', 'Balances on one foot 10+ sec', 'Rides bicycle with training wheels'],
    cognitive: ['Counts to 20+', 'Knows address or phone number', 'Understands calendar days', 'Copies complex shapes', 'Sorts by multiple attributes'],
    language: ['Speaks in complex sentences', 'Retells stories with details', 'Understands jokes', 'Reads simple sight words', 'Writes own name'],
    social: ['Follows multi-step rules', 'Shows competitive spirit', 'Develops strong friendships', 'Understands right from wrong', 'Manages emotions better'],
  },
}

function loadChecked(): string[] { const d = localStorage.getItem(STORAGE_KEY); return d ? JSON.parse(d) : [] }
function saveChecked(ids: string[]) { localStorage.setItem(STORAGE_KEY, JSON.stringify(ids)) }

export default function Milestones() {
  const navigate = useNavigate()
  const ageMonths = useAuthStore(s => s.user?.child_profile?.age_months) || 36
  const childName = useAuthStore(s => s.user?.child_profile?.name) || 'your child'
  const defaultRange = ageMonths < 30 ? '2-3yr' : ageMonths < 42 ? '3-4yr' : ageMonths < 54 ? '4-5yr' : '5-6yr'
  const [ageRange, setAgeRange] = useState(defaultRange)
  const [checked, setChecked] = useState<string[]>(loadChecked)
  const [expanded, setExpanded] = useState<string | null>('physical')

  const milestones = MILESTONES[ageRange]
  const allIds = Object.entries(milestones).flatMap(([cat, items]) => items.map((_, i) => `${ageRange}-${cat}-${i}`))
  const checkedCount = allIds.filter(id => checked.includes(id)).length
  const progress = allIds.length > 0 ? Math.round((checkedCount / allIds.length) * 100) : 0

  const toggle = (id: string) => {
    const updated = checked.includes(id) ? checked.filter(c => c !== id) : [...checked, id]
    setChecked(updated); saveChecked(updated)
  }

  const ageYears = Math.floor(ageMonths / 12)
  const ageRemMonths = ageMonths % 12

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <button className={styles.backButton} onClick={() => navigate(-1)}>←</button>
        <div>
          <h1 className={styles.title}>Is This Normal?</h1>
          <p className={styles.subtitle}>{childName}, {ageYears}y {ageRemMonths}m</p>
        </div>
      </header>

      <div className={styles.ageRow}>
        {AGE_RANGES.map(r => (
          <button key={r} className={`${styles.agePill} ${ageRange === r ? styles.agePillActive : ''}`} onClick={() => setAgeRange(r)}>{r}</button>
        ))}
      </div>

      <div className={styles.progressCard}>
        <div className={styles.progressBar}><div className={styles.progressFill} style={{ width: `${progress}%` }} /></div>
        <p className={styles.progressText}>{checkedCount} of {allIds.length} milestones ({progress}%)</p>
      </div>

      <div className={styles.banner}>Every child develops at their own pace. These are general guidelines, not strict rules. If you have concerns, talk to your pediatrician.</div>

      {CATEGORIES.map(cat => {
        const items = milestones[cat.key] || []
        const isOpen = expanded === cat.key
        return (
          <div key={cat.key} className={styles.category}>
            <button className={styles.categoryHeader} onClick={() => setExpanded(isOpen ? null : cat.key)} style={{ borderLeftColor: cat.color }}>
              <span>{cat.emoji} {cat.label}</span>
              <span className={styles.arrow}>{isOpen ? '▾' : '▸'}</span>
            </button>
            {isOpen && (
              <div className={styles.milestoneList}>
                {items.map((item, i) => {
                  const id = `${ageRange}-${cat.key}-${i}`
                  const isChecked = checked.includes(id)
                  return (
                    <label key={i} className={`${styles.milestoneItem} ${isChecked ? styles.milestoneChecked : ''}`}>
                      <input type="checkbox" checked={isChecked} onChange={() => toggle(id)} className={styles.checkbox} />
                      <span>{item}</span>
                    </label>
                  )
                })}
              </div>
            )}
          </div>
        )
      })}

      {progress < 50 && checkedCount > 0 && (
        <div className={styles.flagBanner}>If you notice several unchecked items, it might be worth mentioning at your next pediatrician visit — just to discuss, not to worry.</div>
      )}
    </div>
  )
}
